import {useMessages} from "i18n";
import type {UpsellCardModel} from "./upsellCard";
import {SlideOutCard} from "./SlideOutCard";
import type {SlideOutCardControls} from "./useSlideOutCards";

export interface PlanUpsellCardProps {
	/** From {@link upsellCard}; null renders nothing. */
	model: UpsellCardModel | null;
	/** Open or tucked, and the lift: owned by the panel through `useSlideOutCards`, never held here. */
	controls: SlideOutCardControls;
	/**
	 * The target tier's monthly price, already formatted in the storefront's currency by the panel —
	 * the same figure and the same storefront the upgrade dialog this card's button opens will quote.
	 */
	price: string;
	onAction: () => void;
}

/**
 * The send screen's upsell card (kitecorp/transferify-ui#2278, #2514): the ask member of the slide-out
 * family, shown at the moment a sender meets a wall and offering the tier that unlocks what they touched.
 *
 * Everything about the shell — the slide, the tucked chip, the ×, the stack placement — belongs to
 * {@link SlideOutCard}, which the download-limit card shares. What lives here is what makes this card
 * the ASK: the brand-blue accent, the arrow on its chip, and which line of the catalogue the wall reads.
 *
 * <b>Title, one line, one button.</b> There is deliberately no third line and no plan card behind a
 * flip (both were studied on the prototype): the reader is mid-send, and the card asks one question.
 */
export function PlanUpsellCard({model, controls, price, onAction}: PlanUpsellCardProps) {
	const {sender, pricing, common} = useMessages();
	const copy = sender.upsellCard;
	if (model === null) {
		return null;
	}
	const plan = pricing.planNames[model.target];
	const {title, line} = copyFor(model, plan, price, copy, sender.options);
	return (
		<SlideOutCard
			accent="upsell"
			open={controls.open}
			lifting={controls.lifting}
			offsetTop={controls.offsetTop}
			stacked={controls.stacked}
			onHeight={controls.onHeight}
			onOpen={controls.openCard}
			onTuck={controls.closeCard}
			testId="upsell-card"
			data-upsell-trigger={model.trigger}
			title={title}
			line={line}
			showLabel={copy.show}
			tuckLabel={common.tuckTag}
			glyph="↑"
			/* The same wording as the warning card's button on purpose: the two can stand on screen
			   together, and one action worded two ways reads as two different offers. */
			actionLabel={sender.limitCard.upgradeTo(plan)}
			onAction={onAction}
		/>
	);
}

/** Which of the catalogue's lines this wall reads, and with which figures. */
function copyFor(
	model: UpsellCardModel,
	plan: string,
	price: string,
	copy: ReturnType<typeof useMessages>["sender"]["upsellCard"],
	optionCopy: ReturnType<typeof useMessages>["sender"]["options"],
): {title: string; line: string} {
	switch (model.trigger) {
		case "password":
			return {title: copy.passwordTitle, line: copy.passwordLine(plan, price)};
		case "expiry":
			return {title: copy.expiryTitle, line: retentionLine(model.days, plan, price, copy)};
		case "maxExpiry":
			return {title: copy.longerTitle(model.fromDays), line: retentionLine(model.days, plan, price, copy)};
		case "option":
			// ONE generic row for every other locked control (owner 2026-09-17, ui#2514), built from the
			// option row's OWN label rather than from per-feature copy: a card that named each feature in
			// its own words would be a second copy matrix to keep in three languages, and it would be
			// wrong the day a row is renamed. The sender reads back exactly the control they touched.
			return {title: optionCopy[model.option].label, line: copy.includedLine(plan, price)};
	}
}

/**
 * What a retention offer says: a day count, or the absence of a window.
 *
 * Shared by both retention rows because they ask the same question — the locked rung the sender
 * reached for may be the indefinite one, exactly as the Premium ceiling row's offer is.
 */
function retentionLine(
	days: number | null,
	plan: string,
	price: string,
	copy: ReturnType<typeof useMessages>["sender"]["upsellCard"],
): string {
	return days === null ? copy.neverLine(plan, price) : copy.daysLine(days, plan, price);
}
